import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { findDocumentBySlug } from '../utils/slugUtils';
import { fetchProducts } from '../services/dataService';
import './ProductDetail.css';

// Related Products Component
const RelatedProducts = ({ category, currentProductId }) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRelatedProducts = async () => {
      setLoading(true);
      try {
        const allProducts = await fetchProducts();
        const related = allProducts
          .filter(p => p.id !== currentProductId && p.category === category)
          .slice(0, 3);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error loading related products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRelatedProducts();
  }, [category, currentProductId]);

  if (loading) {
    return <div className="loading">Loading related products...</div>;
  }

  if (relatedProducts.length === 0) {
    return <p>No related products found.</p>;
  }

  return (
    <div className={`products-grid ${relatedProducts.length <= 2 ? 'few-items' : ''}`}>
      {relatedProducts.map(relatedProduct => (
        <div key={relatedProduct.id} className="product-card">
          <div className="product-image">
            <img src={relatedProduct.image || '/logo.png'} alt={relatedProduct.name} />
          </div>
          <div className="product-info">
            <span className="product-category">{relatedProduct.category}</span>
            <h3 className="product-name">{relatedProduct.name}</h3>
            <div className="product-footer">
              <Link 
                to={`/products/${relatedProduct.slug || relatedProduct.id}`} 
                className="btn btn-primary btn-sm"
              >
                View Details
              </Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProductDetail = () => {
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    // Load product data from Firebase using slug
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const foundProduct = await findDocumentBySlug(productSlug, 'products');
        setProduct(foundProduct);
        
        // Set selectedImage to the index of the main image
        if (foundProduct && foundProduct.image && foundProduct.images) {
          const mainImageIndex = foundProduct.images.findIndex(img => img === foundProduct.image);
          setSelectedImage(mainImageIndex >= 0 ? mainImageIndex : 0);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productSlug]);

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="container">
          <div className="loading-content">
            <div className="loading"></div>
            <p>Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <div className="container">
          <div className="not-found-content">
            <h1>Product Not Found</h1>
            <p>The product you're looking for doesn't exist.</p>
            <Link to="/products" className="btn btn-primary">
              Back to Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail">
      {/* Product Content */}
      <section className="product-content">
        <div className="container">
          <div className="product-grid">
            {/* Product Images */}
            <div className="product-images">
              <div className="main-image">
                <img src={product.images[selectedImage]} alt={product.name} />
              </div>
              <div className="thumbnail-images">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="product-info">
              <div className="product-header">
                <span className="product-category">{product.category}</span>
                <h1 className="product-title">{product.name}</h1>
                <div className="product-rating">
                  <div className="stars">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={`star ${i < Math.floor(product.rating) ? 'filled' : ''}`}>
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="rating-text">{product.rating} ({product.reviews} reviews)</span>
                </div>
              </div>


              <div className="purchase-options">
                <h3>Click to Buy</h3>
                <div className="ecommerce-links">
                  {product.ecommerceLinks && product.ecommerceLinks.length > 0 ? (
                    product.ecommerceLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ecommerce-link"
                      >
                        <span className="platform-name">{link.platform}</span>
                        {link.quantity && (
                          <span className="quantity-badge">{link.quantity}</span>
                        )}
                      </a>
                    ))
                  ) : (
                    <p className="no-links">No purchase links available</p>
                  )}
                </div>
              </div>

              <div className="product-description">
                <h3>Description</h3>
                <p>{product.fullDescription}</p>
              </div>

              <div className="product-benefits">
                <h3>Key Benefits</h3>
                <ul>
                  {product.benefits && product.benefits.length > 0 ? (
                    product.benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))
                  ) : (
                    <li>No benefits listed</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="related-products">
        <div className="container">
          <h2 className="section-title">Related Products</h2>
          <RelatedProducts category={product.category} currentProductId={product.id} />
        </div>
      </section>

      {/* Call to Action */}
      <section className="product-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Need Help Choosing?</h2>
            <p>Our gardening experts are here to help you select the right products.</p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <Link to="/products" className="btn btn-primary">
                Browse All Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;